import SvgCircleChecked from 'creative-ui/uikit/svg/basic/circle-checked';
import SvgCircleAlert from 'creative-ui/uikit/svg/circle-alert';
import SvgPucePoint from 'creative-ui/uikit/svg/puce-point';

import './PasswordCheck.css';

import Text from '../Typography/Text/Text';

export interface PasswordComplexityRule {
  code: string;
  label: string;
  isValid: boolean;
  items?: PasswordComplexityRule[];
}

export interface PasswordCheckProps {
  /** Array of password complexity rules to display. */
  passwordComplexityRules?: PasswordComplexityRule[];
  /** Header text displayed above the password criteria list. */
  headerText?: string;
  /** Text announced to screen readers when a requirement becomes valid. */
  validLabel?: string;
  /** Optional description id for the component container. Allows linking to the input. */
  descriptionId?: string;
  /**
   * When `false`, every rule renders a neutral bullet icon regardless of
   * `isValid` (e.g. before the first submit attempt). When `true`, rules
   * render a check icon when valid or a red "X" circle when invalid.
   * Defaults to `true`.
   */
  showValidation?: boolean;
}

function RuleItem({
  rule,
  validLabel,
  showValidation,
}: Readonly<{
  rule: PasswordComplexityRule;
  validLabel: string;
  showValidation: boolean;
}>) {
  const Icon = !showValidation
    ? SvgPucePoint
    : rule.isValid
      ? SvgCircleChecked
      : SvgCircleAlert;
  const iconClassName = !showValidation
    ? 'c-passwordCheck__icon c-passwordCheck__icon--neutral'
    : rule.isValid
      ? 'c-passwordCheck__icon c-passwordCheck__icon--valid'
      : 'c-passwordCheck__icon c-passwordCheck__icon--invalid';

  return (
    <li>
      <div className="c-passwordCheck__item">
        <Icon className={iconClassName} width={16} height={16} />
        <p className="c-passwordCheck__text">
          {rule.label}
          {showValidation && rule.isValid && (
            <span className="c-passwordCheck__visuallyHidden">
              {` ${validLabel}`}
            </span>
          )}
        </p>
      </div>
      {rule.items && rule.items.length > 0 && (
        <ul className="c-passwordCheck__subList">
          {rule.items.map((item) => (
            <RuleItem
              key={item.code}
              rule={item}
              validLabel={validLabel}
              showValidation={showValidation}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * `PasswordCheck`. `creative-ui` has no equivalent, so this reimplements the
 * same API on top of `creative-ui` icons and design tokens.
 */
export function PasswordCheck({
  passwordComplexityRules,
  headerText,
  descriptionId,
  validLabel = 'requirement met',
  showValidation = true,
}: Readonly<PasswordCheckProps>) {
  if (!passwordComplexityRules || passwordComplexityRules.length === 0) {
    return null;
  }

  return (
    <section className="c-passwordCheck" aria-labelledby={descriptionId}>
      <Text bold size="md" className="c-passwordCheck__header">
        {headerText}
      </Text>
      <ul className="c-passwordCheck__list" aria-live="polite">
        {passwordComplexityRules.map((rule) => (
          <RuleItem
            key={rule.code}
            rule={rule}
            validLabel={validLabel}
            showValidation={showValidation}
          />
        ))}
      </ul>
    </section>
  );
}

export default PasswordCheck;
